import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '@/hooks/useNotifications';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import type { Notification } from '@/hooks/useNotifications';

function fmtTime(d: string) {
  const date = new Date(d);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min atrás`;
  if (diffH < 24) return `${diffH}h atrás`;
  if (diffD < 7) return `${diffD}d atrás`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function NotifIcon({ type }: { type: string }) {
  if (type === 'comment') return <i className="ri-chat-3-line text-cyan-500 text-sm"></i>;
  return <i className="ri-notification-3-line text-slate-400 text-sm"></i>;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { profile } = useAuthContext();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleClick = async (n: Notification) => {
    if (!n.read) await markAsRead(n.id);
    setOpen(false);

    if (n.type === 'whatsapp_message') {
      if (n.lead_id) {
        navigate('/admin/leads', { state: { openLeadId: n.lead_id, openTab: 'whatsapp' } });
      } else if (n.client_id) {
        navigate('/admin/clientes', { state: { openClientId: n.client_id, openTab: 'whatsapp' } });
      }
      return;
    }

    if (!n.project_id) return;

    const isAdmin = profile?.role === 'admin';

    if (isAdmin) {
      // Fetch task board_status to decide where to navigate
      let boardStatus = 'backlog';
      if (n.task_id) {
        const { data: taskData } = await supabase
          .from('tasks')
          .select('board_status')
          .eq('id', n.task_id)
          .maybeSingle();
        if (taskData?.board_status) boardStatus = taskData.board_status;
      }

      if (boardStatus === 'backlog') {
        navigate(
          `/admin/scrum/${n.project_id}/backlog`,
          { state: { openTaskId: n.task_id } }
        );
      } else {
        navigate(
          `/admin/scrum/${n.project_id}/board`,
          { state: { openTaskId: n.task_id } }
        );
      }
    } else {
      // Client: go to project page, backlog tab, task pre-opened
      navigate(
        `/app/projeto/${n.project_id}`,
        { state: { tab: 'backlog', openTaskId: n.task_id } }
      );
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(v => !v)}
        className="relative w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
        title="Notificações"
      >
        <i className="ri-notification-3-line text-base"></i>
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-80 bg-white rounded-xl border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <span className="text-slate-800 font-semibold text-sm">Notificações</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-bold">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-cyan-600 hover:text-cyan-700 cursor-pointer whitespace-nowrap"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-slate-400">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100">
                  <i className="ri-notification-3-line text-slate-300 text-lg"></i>
                </div>
                <p className="text-xs text-slate-400">Nenhuma notificação</p>
              </div>
            ) : (
              notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors cursor-pointer border-b border-slate-50 last:border-0 ${
                    !n.read ? 'bg-cyan-50/40' : ''
                  }`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 mt-0.5 ${
                    !n.read ? 'bg-cyan-100' : 'bg-slate-100'
                  }`}>
                    <NotifIcon type={n.type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs leading-snug ${!n.read ? 'text-slate-800 font-semibold' : 'text-slate-600 font-medium'}`}>
                      {n.title}
                    </p>
                    {n.body && (
                      <p className="text-xs text-slate-400 mt-0.5 truncate">{n.body}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">{fmtTime(n.created_at)}</p>
                  </div>
                  {!n.read && (
                    <div className="w-2 h-2 rounded-full bg-cyan-500 shrink-0 mt-1.5"></div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
